import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Search, Upload, Mail, Send, BarChart3, Users, Globe, Zap,
  Play, Pause, Eye, Target, TrendingUp, FileText, Languages,
  RefreshCw, Plus, Trash2, ChevronRight, Flame, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProspectionAutoPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('prospects');
  const [prospects, setProspects] = useState({ prospects: [], stats: {} });
  const [campagnes, setCampagnes] = useState([]);
  const [tracking, setTracking] = useState({ stats: {}, emails: [], prospects_chauds: [] });
  const [loading, setLoading] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState({ secteur: 'agriculture', zone_geo: 'France', mots_cles: '', nb_resultats: 20 });
  const [searching, setSearching] = useState(false);

  // Campaign
  const [showNewCamp, setShowNewCamp] = useState(false);
  const [newCamp, setNewCamp] = useState({ nom: '', objet_email: '', corps_email: '', langue: 'fr', quota_jour: 20 });

  // Translate
  const [showTranslate, setShowTranslate] = useState(false);
  const [translateData, setTranslateData] = useState({ objet: '', corps: '', langue_cible: 'en' });
  const [translateResult, setTranslateResult] = useState(null);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    const sid = localStorage.getItem('activeSocieteId') || user?.exploitation_id || '';
    if (sid) h['X-Societe-Id'] = sid;
    return h;
  }, [token, user]);

  const fetchProspects = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/prospection-auto/prospects`, { headers: headers() });
      if (res.ok) setProspects(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchCampagnes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/prospection-auto/campagnes`, { headers: headers() });
      if (res.ok) { const d = await res.json(); setCampagnes(d.campagnes || []); }
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchTracking = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/prospection-auto/tracking`, { headers: headers() });
      if (res.ok) setTracking(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => {
    fetchProspects();
    fetchCampagnes();
    fetchTracking();
  }, [fetchProspects, fetchCampagnes, fetchTracking]);

  const rechercherProspects = async () => {
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/prospection-auto/recherche`, {
        method: 'POST', headers: headers(), body: JSON.stringify(searchQuery)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.prospects_sauvegardes} prospects trouves et sauvegardes`);
        fetchProspects();
      }
    } catch (e) { toast.error('Erreur recherche'); }
    setSearching(false);
  };

  const creerCampagne = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prospection-auto/campagnes`, {
        method: 'POST', headers: headers(), body: JSON.stringify(newCamp)
      });
      if (res.ok) {
        toast.success('Campagne creee');
        setShowNewCamp(false);
        setNewCamp({ nom: '', objet_email: '', corps_email: '', langue: 'fr', quota_jour: 20 });
        fetchCampagnes();
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const lancerCampagne = async (id) => {
    const res = await fetch(`${API_URL}/api/prospection-auto/campagnes/${id}/lancer`, {
      method: 'PUT', headers: headers()
    });
    if (res.ok) { const d = await res.json(); toast.success(`Campagne lancee — ${d.nb_prospects} prospects`); fetchCampagnes(); }
  };

  const pauserCampagne = async (id) => {
    await fetch(`${API_URL}/api/prospection-auto/campagnes/${id}/pause`, { method: 'PUT', headers: headers() });
    toast.success('Campagne en pause');
    fetchCampagnes();
  };

  const traduireEmail = async () => {
    setTranslateResult(null);
    try {
      const res = await fetch(`${API_URL}/api/prospection-auto/traduire-email`, {
        method: 'POST', headers: headers(), body: JSON.stringify(translateData)
      });
      if (res.ok) { setTranslateResult(await res.json()); }
    } catch (e) { toast.error('Erreur traduction'); }
  };

  const adapterEmail = async (campagneId, objet, corps) => {
    try {
      const res = await fetch(`${API_URL}/api/prospection-auto/adapter-email`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ campagne_id: campagneId, objet, corps })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.conseil || 'Email adapte par IA');
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const importCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const h = {};
      if (token) h['Authorization'] = `Bearer ${token}`;
      const sid = localStorage.getItem('activeSocieteId') || user?.exploitation_id || '';
      if (sid) h['X-Societe-Id'] = sid;
      const res = await fetch(`${API_URL}/api/prospection-auto/import-csv`, { method: 'POST', headers: h, body: formData });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${d.nb_importes} prospects importes (${d.nb_doublons} doublons, ${d.nb_erreurs} erreurs)`);
        fetchProspects();
      }
    } catch (err) { toast.error('Erreur import'); }
  };

  const stats = tracking?.stats || {};
  const pStats = prospects?.stats || {};

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white" data-testid="prospection-auto-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-[#0d1321]/80 backdrop-blur-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Prospection Automatisee</h1>
              <p className="text-xs text-slate-400">Recherche, campagnes, emails, tracking & adaptation IA</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-4 space-y-4">
        {/* Stats rapides */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatBox icon={Users} label="Prospects" value={pStats.total || 0} color="violet" />
          <StatBox icon={Mail} label="Emails envoyes" value={stats.envoyes || 0} color="blue" />
          <StatBox icon={Eye} label="Ouverts" value={stats.ouverts || 0} color="emerald" />
          <StatBox icon={TrendingUp} label="Taux ouverture" value={`${stats.taux_ouverture || 0}%`} color="amber" />
          <StatBox icon={Flame} label="Prospects chauds" value={stats.prospects_chauds || 0} color="red" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            { key: 'prospects', label: `Prospects (${pStats.total || 0})`, icon: Users },
            { key: 'recherche', label: 'Rechercher', icon: Search },
            { key: 'campagnes', label: `Campagnes (${campagnes.length})`, icon: Mail },
            { key: 'tracking', label: 'Tracking', icon: BarChart3 },
            { key: 'outils', label: 'Traduction & IA', icon: Languages },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.key ? 'bg-violet-500/20 text-violet-400 border border-violet-500/40' : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`} data-testid={`tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* PROSPECTS TAB */}
        {activeTab === 'prospects' && (
          <div data-testid="prospects-tab">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-violet-400 uppercase">Base de Prospects</h2>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input type="file" accept=".csv" onChange={importCSV} className="hidden" data-testid="import-csv-input" />
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-slate-300 transition-colors">
                    <Upload className="w-3.5 h-3.5" /> Import CSV
                  </span>
                </label>
                <Button size="sm" onClick={() => setActiveTab('recherche')} className="bg-violet-600 hover:bg-violet-700 text-xs">
                  <Search className="w-3.5 h-3.5 mr-1" /> Rechercher
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {(prospects.prospects || []).slice(0, 50).map(p => (
                <div key={p.id} className="rounded-lg border border-slate-800 bg-slate-800/20 p-3 flex items-center gap-3" data-testid={`prospect-${p.id}`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${
                    p.statut === 'chaud' ? 'bg-red-500' : p.statut === 'contacte' ? 'bg-blue-500' : p.statut === 'converti' ? 'bg-emerald-500' : 'bg-slate-500'
                  }`} />
                  <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                    <span className="font-medium text-white truncate">{p.raison_sociale || 'N/A'}</span>
                    <span className="text-slate-400 truncate">{p.email || 'Pas d\'email'}</span>
                    <span className="text-slate-400">{p.ville || p.code_postal || ''} {p.departement ? `(${p.departement})` : ''}</span>
                    <span className="text-slate-500 text-[10px]">{p.source} — {p.created_at?.substring(0, 10)}</span>
                  </div>
                  <Badge className={`text-[10px] ${
                    p.statut === 'chaud' ? 'bg-red-500' : p.statut === 'contacte' ? 'bg-blue-600' : p.statut === 'converti' ? 'bg-emerald-600' : 'bg-slate-600'
                  }`}>{p.statut}</Badge>
                </div>
              ))}
              {(!prospects.prospects || prospects.prospects.length === 0) && (
                <div className="text-center py-12 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun prospect. Lancez une recherche ou importez un CSV.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* RECHERCHE TAB */}
        {activeTab === 'recherche' && (
          <div data-testid="recherche-tab" className="max-w-2xl">
            <h2 className="text-sm font-bold text-violet-400 uppercase mb-4">Recherche Automatique de Prospects</h2>
            <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Secteur</label>
                  <input value={searchQuery.secteur} onChange={e => setSearchQuery({...searchQuery, secteur: e.target.value})}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm text-white" placeholder="agriculture, viticulture..." />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Zone geographique</label>
                  <input value={searchQuery.zone_geo} onChange={e => setSearchQuery({...searchQuery, zone_geo: e.target.value})}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm text-white" placeholder="France, Vaucluse..." />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Mots-cles</label>
                <input value={searchQuery.mots_cles} onChange={e => setSearchQuery({...searchQuery, mots_cles: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm text-white" placeholder="viticulteur, maraicher, bio, HVE..." data-testid="search-keywords" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Nombre de resultats</label>
                <input type="number" value={searchQuery.nb_resultats} onChange={e => setSearchQuery({...searchQuery, nb_resultats: parseInt(e.target.value) || 20})}
                  className="w-32 bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm text-white" />
              </div>
              <Button onClick={rechercherProspects} disabled={searching} className="bg-violet-600 hover:bg-violet-700 w-full" data-testid="search-btn">
                {searching ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                {searching ? 'Recherche en cours...' : 'Lancer la recherche'}
              </Button>
              <p className="text-[10px] text-slate-500">Utilise l'API entreprises data.gouv.fr + enrichissement IA pour trouver des contacts.</p>
            </div>
          </div>
        )}

        {/* CAMPAGNES TAB */}
        {activeTab === 'campagnes' && (
          <div data-testid="campagnes-tab">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-violet-400 uppercase">Campagnes Email</h2>
              <Button onClick={() => setShowNewCamp(true)} className="bg-violet-600 hover:bg-violet-700 text-xs" data-testid="new-campaign-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle campagne
              </Button>
            </div>

            {showNewCamp && (
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 mb-4 space-y-3">
                <input value={newCamp.nom} onChange={e => setNewCamp({...newCamp, nom: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white" placeholder="Nom de la campagne" data-testid="camp-nom" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={newCamp.langue} onChange={e => setNewCamp({...newCamp, langue: e.target.value})}
                    className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white">
                    <option value="fr">Francais</option><option value="en">English</option>
                    <option value="es">Espanol</option><option value="de">Deutsch</option>
                    <option value="it">Italiano</option><option value="pt">Portugues</option>
                    <option value="ar">Arabe</option><option value="nl">Nederlands</option>
                  </select>
                  <input type="number" value={newCamp.quota_jour} onChange={e => setNewCamp({...newCamp, quota_jour: parseInt(e.target.value) || 20})}
                    className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white" placeholder="Quota/jour" />
                </div>
                <input value={newCamp.objet_email} onChange={e => setNewCamp({...newCamp, objet_email: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white" placeholder="Objet de l'email — ex: {{nom_entreprise}}, simplifiez votre gestion" data-testid="camp-objet" />
                <textarea value={newCamp.corps_email} onChange={e => setNewCamp({...newCamp, corps_email: e.target.value})}
                  className="w-full h-32 bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white resize-none" placeholder="Corps de l'email... Variables: {{nom_entreprise}}, {{prenom_dirigeant}}" data-testid="camp-corps" />
                <div className="flex gap-2">
                  <Button onClick={creerCampagne} className="bg-violet-600 hover:bg-violet-700 text-xs" data-testid="create-camp-btn">Creer</Button>
                  <Button variant="outline" onClick={() => setShowNewCamp(false)} className="border-slate-600 text-slate-300 text-xs">Annuler</Button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {campagnes.map(camp => (
                <div key={camp.id} className="rounded-xl border border-slate-700 bg-slate-800/20 p-4" data-testid={`campagne-${camp.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-violet-400" />
                      <span className="font-bold text-sm text-white">{camp.nom}</span>
                      <Badge className={`text-[10px] ${
                        camp.statut === 'en_cours' ? 'bg-emerald-600' : camp.statut === 'prete' ? 'bg-blue-600' : camp.statut === 'pause' ? 'bg-amber-600' : camp.statut === 'terminee' ? 'bg-slate-600' : 'bg-slate-700'
                      }`}>{camp.statut}</Badge>
                      <Badge variant="outline" className="text-[10px] border-slate-600"><Globe className="w-3 h-3 inline mr-0.5" />{camp.langue}</Badge>
                    </div>
                    <div className="flex gap-2">
                      {camp.statut === 'brouillon' && (
                        <Button size="sm" onClick={() => lancerCampagne(camp.id)} className="bg-emerald-600 hover:bg-emerald-700 text-xs">
                          <Play className="w-3 h-3 mr-1" /> Lancer
                        </Button>
                      )}
                      {(camp.statut === 'prete' || camp.statut === 'en_cours') && (
                        <Button size="sm" variant="outline" onClick={() => pauserCampagne(camp.id)} className="border-amber-500/40 text-amber-400 text-xs">
                          <Pause className="w-3 h-3 mr-1" /> Pause
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => adapterEmail(camp.id, camp.variantes_email?.[0]?.objet, camp.variantes_email?.[0]?.corps)}
                        className="border-violet-500/40 text-violet-400 text-xs">
                        <Zap className="w-3 h-3 mr-1" /> Adapter IA
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs text-slate-400">
                    <span>Quota: {camp.quota_jour}/jour</span>
                    <span>Envoyes: {camp.emails_envoyes || 0}</span>
                    <span>Prospects: {camp.nb_prospects || camp.prospect_ids?.length || 0}</span>
                  </div>
                </div>
              ))}
              {campagnes.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Aucune campagne. Creez-en une !</p>}
            </div>
          </div>
        )}

        {/* TRACKING TAB */}
        {activeTab === 'tracking' && (
          <div data-testid="tracking-tab">
            <h2 className="text-sm font-bold text-violet-400 uppercase mb-4">Tracking Emails</h2>
            
            {/* Prospects chauds */}
            {(tracking.prospects_chauds || []).length > 0 && (
              <div className="rounded-xl border-2 border-red-500/30 bg-red-500/5 p-4 mb-4">
                <h3 className="text-sm font-bold text-red-400 flex items-center gap-2 mb-3">
                  <Flame className="w-4 h-4" /> Prospects Chauds — A APPELER
                </h3>
                <div className="space-y-2">
                  {tracking.prospects_chauds.map(p => (
                    <div key={p.tracking_id || p.id} className="flex items-center gap-3 bg-red-500/10 rounded-lg px-3 py-2">
                      <Flame className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-white">{p.nom_prospect || p.email_prospect}</span>
                      <span className="text-xs text-red-300">{p.nb_ouvertures} ouvertures</span>
                      <span className="text-xs text-slate-400 ml-auto">{p.email_prospect}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Emails list */}
            <div className="space-y-2">
              {(tracking.emails || []).slice(0, 30).map(e => (
                <div key={e.id} className={`rounded-lg border p-3 flex items-center gap-3 text-xs ${
                  e.statut === 'ouvert' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-800 bg-slate-800/20'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${e.statut === 'ouvert' ? 'bg-emerald-500' : 'bg-slate-500'}`} />
                  <span className="text-white font-medium truncate w-40">{e.nom_prospect || e.prospect_nom}</span>
                  <span className="text-slate-400 truncate w-40">{e.email_prospect}</span>
                  <span className="text-slate-400 truncate flex-1">{e.sujet}</span>
                  <Badge className={`text-[9px] ${e.statut === 'ouvert' ? 'bg-emerald-600' : e.statut === 'envoye' ? 'bg-blue-600' : 'bg-slate-600'}`}>
                    {e.statut} {(e.nb_ouvertures || 0) > 0 ? `(${e.nb_ouvertures}x)` : ''}
                  </Badge>
                </div>
              ))}
              {(!tracking.emails || tracking.emails.length === 0) && <p className="text-sm text-slate-500 text-center py-8">Aucun email envoye pour le moment.</p>}
            </div>
          </div>
        )}

        {/* OUTILS IA TAB */}
        {activeTab === 'outils' && (
          <div data-testid="outils-tab" className="max-w-2xl space-y-6">
            <div>
              <h2 className="text-sm font-bold text-violet-400 uppercase mb-4">Traduction Email</h2>
              <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4 space-y-3">
                <select value={translateData.langue_cible} onChange={e => setTranslateData({...translateData, langue_cible: e.target.value})}
                  className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white">
                  <option value="en">English</option><option value="es">Espanol</option>
                  <option value="de">Deutsch</option><option value="it">Italiano</option>
                  <option value="pt">Portugues</option><option value="ar">Arabe</option>
                  <option value="nl">Nederlands</option><option value="zh">Chinois</option>
                </select>
                <input value={translateData.objet} onChange={e => setTranslateData({...translateData, objet: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white" placeholder="Objet a traduire" />
                <textarea value={translateData.corps} onChange={e => setTranslateData({...translateData, corps: e.target.value})}
                  className="w-full h-24 bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white resize-none" placeholder="Corps de l'email a traduire..." />
                <Button onClick={traduireEmail} className="bg-violet-600 hover:bg-violet-700 text-xs" data-testid="translate-btn">
                  <Languages className="w-4 h-4 mr-1" /> Traduire
                </Button>
                {translateResult && (
                  <div className="bg-slate-900 rounded-lg p-3 border border-emerald-500/30 mt-2">
                    <p className="text-xs text-emerald-400 font-bold mb-1">Traduction ({translateResult.langue}):</p>
                    <p className="text-sm text-white font-medium">{translateResult.objet}</p>
                    <p className="text-xs text-slate-300 mt-1 whitespace-pre-line">{translateResult.corps}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-sm font-bold text-violet-400 uppercase mb-4">Adaptation IA</h2>
              <p className="text-xs text-slate-400 mb-3">L'IA analyse le taux d'ouverture de vos campagnes et reecrit automatiquement les emails pour ameliorer les performances.</p>
              <div className="space-y-2">
                {campagnes.filter(c => c.variantes_email?.length).map(camp => (
                  <div key={camp.id} className="rounded-lg border border-slate-700 bg-slate-800/20 p-3 flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-white">{camp.nom}</span>
                      <p className="text-[10px] text-slate-400">Taux: {camp.taux_ouverture || 0}% — {camp.emails_envoyes || 0} envoyes</p>
                    </div>
                    <Button size="sm" onClick={() => adapterEmail(camp.id, camp.variantes_email[0].objet, camp.variantes_email[0].corps)}
                      className="bg-violet-600 hover:bg-violet-700 text-xs" data-testid={`adapt-${camp.id}`}>
                      <Zap className="w-3 h-3 mr-1" /> Optimiser
                    </Button>
                  </div>
                ))}
                {campagnes.length === 0 && <p className="text-xs text-slate-500">Creez d'abord une campagne pour l'optimiser.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  const colors = {
    violet: 'from-violet-500/20 to-violet-500/5 border-violet-500/30 text-violet-400',
    blue: 'from-blue-500/20 to-blue-500/5 border-blue-500/30 text-blue-400',
    emerald: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-400',
    amber: 'from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-400',
    red: 'from-red-500/20 to-red-500/5 border-red-500/30 text-red-400',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-b p-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-xs opacity-70">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}
